import {
  SELECTOR_VISUAL_RAIL_PX,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
} from "@salestrak/ui";
import type { EntityRefValue } from "#app/lib/data";
import {
  entityFromAssigneeRef,
  iconSourceToName,
  resolveEntityLabel,
  type EntityData,
} from "#app/lib/entity-visuals";
import { isInvited, isFormerMember } from "#app/lib/role-utils";
import { EntityVisual } from "#app/components/entity";

/**
 * THE visual for a selector-row: every pool fills the row's visual rail, so a
 * member photo, a team's key-color tile and a stage pie all read at the same
 * weight in the same list. The rail is the library's number — a per-pool size
 * choice means a 32px team tile beside a 20px member avatar in the same
 * popover, once two pools disagree.
 *
 * `avatarSize` pins the circle to the rail; the semantic `size` still drives
 * the glyph inside a tile, which sits a step under the box it fills.
 */
export function poolItemVisual(entity: EntityData) {
  return (
    <EntityVisual
      entity={entity}
      size="sm"
      avatarSize="sm"
      boxSize={SELECTOR_VISUAL_RAIL_PX}
      // The option's title names it; the visual beside it only decorates.
      decorative
    />
  );
}

export function normalizeValue(value: string | string[] | null | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

/** `items` plus the row `itemFor` resolves for each of `ids` it does not hold yet. */
export function appendMissing(
  items: EntityItemConfig[],
  ids: Iterable<string>,
  itemFor: (id: string) => EntityItemConfig | undefined,
): EntityItemConfig[] {
  const known = new Set(items.map((item) => item.id));
  const extra: EntityItemConfig[] = [];
  for (const id of ids) {
    if (known.has(id)) continue;
    const item = itemFor(id);
    if (!item) continue;
    known.add(id);
    extra.push(item);
  }
  return extra.length ? [...items, ...extra] : items;
}

/** Each of `ids` as a selection: a loaded or pinned row first, else a pool's
 *  `resolveItem` (a pick past a windowed pool's loaded pages). Ids no pool
 *  knows are left out. */
export function selectionsFromIds(
  pools: readonly EntityPoolConfig[],
  ids: readonly string[],
): EntitySelection[] {
  const loaded = new Map<string, EntitySelection>();
  for (const pool of pools) {
    for (const entity of [...(pool.pinnedItems ?? []), ...pool.items]) {
      loaded.set(entity.id, { id: entity.id, poolId: pool.id, entity });
    }
  }
  return ids.flatMap((id) => {
    const hit = loaded.get(id);
    if (hit) return [hit];
    for (const pool of pools) {
      const entity = pool.resolveItem?.(id);
      if (entity) return [{ id, poolId: pool.id, entity }];
    }
    return [];
  });
}

export function selectionToUserEntity(sel: EntitySelection): EntityData {
  const e = sel.entity;
  return {
    kind: "user",
    id: sel.id,
    user: {
      name: e.title,
      profileImage: e.avatar?.src ?? null,
      email: (e.data?.email as string | null | undefined) ?? null,
      memberStatus: (e.data?.memberStatus as string | null | undefined) ?? null,
    },
  };
}

// Teams key their label as `teamName`; workflow/issueType use `name`.
function selectionToIconEntity<K extends "team" | "workflow" | "issueType">(
  sel: EntitySelection,
  kind: K,
): EntityData {
  const e = sel.entity;
  const icon = {
    icon: iconSourceToName(e.avatar?.icon),
    color: e.avatar?.color ?? null,
  };
  if (kind === "team") {
    return { kind: "team", id: sel.id, team: { teamName: e.title, icon } };
  }
  return {
    kind,
    id: sel.id,
    [kind]: { name: e.title, icon },
  } as EntityData;
}

export function selectionToTeamEntity(sel: EntitySelection): EntityData {
  return selectionToIconEntity(sel, "team");
}

export function selectionToWorkflowEntity(sel: EntitySelection): EntityData {
  return selectionToIconEntity(sel, "workflow");
}

export function selectionToIssueTypeEntity(sel: EntitySelection): EntityData {
  return selectionToIconEntity(sel, "issueType");
}

// Issue sources have no per-item glyph; the label renders on its own (no icon
// passed, so EntityVisual renders nothing).
export function selectionToIssueSourceEntity(sel: EntitySelection): EntityData {
  return {
    kind: "issueSource",
    id: sel.id,
    issueSource: {
      name: sel.entity.title,
      icon: {
        icon: iconSourceToName(sel.entity.avatar?.icon),
        color: sel.entity.avatar?.color ?? null,
      },
    },
  };
}

// Issues/projects key their label as `name`; the workflow rides in `data` so the
// resolved entity keeps the stage→workflow link a chip may need.
export function selectionToIssueEntity(sel: EntitySelection): EntityData {
  const e = sel.entity;
  return {
    kind: "issue",
    id: sel.id,
    issue: {
      name: e.title,
      icon: {
        icon: iconSourceToName(e.avatar?.icon),
        color: e.avatar?.color ?? null,
      },
    },
    workflowId: (e.data?.workflowId as string | null | undefined) ?? undefined,
  };
}

export function selectionToStageEntity(
  sel: EntitySelection,
  workflowId?: string,
): EntityData {
  const e = sel.entity;
  const icon = e.data?.icon as
    | { color?: string | null; percentage?: number | null }
    | undefined;
  return {
    kind: "stage",
    id: sel.id,
    stage: {
      name: e.title,
      type: (e.data?.type as string | null | undefined) ?? null,
      icon: icon ?? null,
    },
    workflowId,
  };
}

// Assignees are members-only, so an assignee ref is always a user.
// `render` drives the display; the primitive src stays as data so selections
// round-trip through selectionToUserEntity.
export function assigneeRefToEntityItem(ref: EntityRefValue): EntityItemConfig {
  const entity = entityFromAssigneeRef(ref);
  return {
    id: ref.entityId,
    title: ref.user ? resolveEntityLabel(entity) : ref.entityId,
    description: ref.isPrimary ? "Primary" : undefined,
    muted: isFormerMember(ref.user),
    invited: isInvited(ref.user),
    avatar: {
      src: ref.user?.profileImage ?? undefined,
      render: () => poolItemVisual(entity),
    },
    keywords: ref.user?.email ? [ref.user.email] : undefined,
    data: {
      email: ref.user?.email ?? undefined,
      entityType: ref.entityType,
      isPrimary: ref.isPrimary ?? false,
      memberStatus: ref.user?.memberStatus ?? null,
    },
  };
}
